import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CustomSettings,
  getDefaultSectionOrderForTemplateType,
  isProformaInvoiceTemplateType,
} from "subModule/src/services/invoice/types";
import dayjs from "dayjs";
import { useDefaultLabels } from "./hooks/useDefaultLabels";
import { ModernInvoiceViewData } from "./mapInvoiceToModernData";

/** Modern template palette — keep in sync with POS when designVariant column is 2 / key "modern". */
export const MODERN_TEMPLATE_THEME = {
  brand: "#2D2C5D",
  brandSoft: "#3D3B6E",
  accent: "#FF6B3D",
  badgeBg: "#F8E8EE",
  badgeText: "#5C2A4A",
  muted: "#6B7A8D",
  text: "#1A1D26",
  sectionBg: "#F8F9FA",
  tableHead: "#EEF1F5",
  border: "#E3E8EE",
  summaryRowBg: "#F6F6F9",
  totalBg: "#E2E8F0",
  totalText: "#FF6B3D",
  paidBg: "#E8F5E9",
  paidText: "#2E7D32",
  dueBg: "#FCE4EC",
  dueText: "#C62828",
  paymentHead: "#D1E4F3",
  paymentHeadText: "#1A3A5C",
  plateBorder: "#E53935",
  trnChipBg: "#EEF1F5",
  pageBg: "#F4F6F8",
} as const;

interface ModernInvoicePreviewProps {
  settings: CustomSettings;
  theme: any;
  formData?: any;
  templateType?: number;
  /** Real invoice view model; when omitted, dummy customizer preview data is used. */
  data?: ModernInvoiceViewData;
  logoSrc?: string;
  /** Currency code shown to the right of amounts in summary / paid / due (e.g. "AED"). */
  currencySymbol?: string;
}

const ModernInvoicePreview: React.FC<ModernInvoicePreviewProps> = ({
  settings,
  theme,
  templateType = 2,
  data,
  logoSrc,
  currencySymbol = "AED",
}) => {
  const isProforma = isProformaInvoiceTemplateType(templateType);
  const { t } = useTranslation();
  const {
    getTableColumnLabel,
    getConsolidatedTableColumnLabel,
    getSummaryLabel,
    getPaymentLabel,
    getTermsLabel,
    getFooterLabel,
    getVehicleLabel,
    getTableSectionLabel,
  } = useDefaultLabels();

  const C = MODERN_TEMPLATE_THEME;
  const brand = theme?.primary || C.brand;

  const getInvoiceNumber = () => {
    const dateStr = "2026-10-1608";
    if (templateType === 3) return `JC-${dateStr}`;
    if (templateType === 4) return `WO-${dateStr}`;
    if (templateType === 5) return t("inspectionForm");
    if (templateType === 6) return `DN-${dateStr}`;
    if (isProforma) return `P-INV-${dateStr}`;
    return `INV-${dateStr}`;
  };

  const getDocTitle = () => {
    if (templateType === 3)
      return {
        primary: settings.jobcardHeadingPrimary || "JOB CARD",
        secondary: settings.jobcardHeadingSecondary || "بطاقة العمل",
      };
    if (templateType === 4)
      return {
        primary: settings.workorderHeadingPrimary || "WORK ORDER",
        secondary: settings.workorderHeadingSecondary || "أمر العمل",
      };
    if (templateType === 6)
      return {
        primary: settings.deliveryNoteHeadingPrimary || "DELIVERY NOTE",
        secondary: settings.deliveryNoteHeadingSecondary || "مذكرة التسليم",
      };
    if (isProforma)
      return {
        primary: settings.proformaInvoiceHeadingPrimary || "PROFORMA INVOICE",
        secondary: settings.proformaInvoiceHeadingSecondary || "فاتورة مبدئية",
      };
    if (templateType === 5)
      return {
        primary: "INSPECTION",
        secondary: "فحص",
      };
    return {
      primary: settings.invoiceHeadingPrimary || "TAX INVOICE",
      secondary: settings.invoiceHeadingSecondary || "فاتورة ضريبية",
    };
  };

  const dummyData = {
    invoiceNo: getInvoiceNumber(),
    date: new Date().toISOString(),
    status: "PAYMENT DUE",
    statusAr: "مستحق الدفع",
    company: {
      name: "InoPressa Automotive Services",
      tagline: "AUTOMOTIVE REPAIR & SERVICE",
      phone: "+971 50 000 0000",
      email: "hello@inopressa.com",
      address: "Shop 12, Al Quoz Industrial, Dubai, UAE",
      vat: "TRN: 100000000000003",
    },
    customer: {
      name: "Ahmed Al Mansoori",
      phone: "+971 55 123 4567",
      email: "ahmed.m@example.com",
      address: "Villa 22, Arabian Ranches, Dubai",
      vat: "TRN: 100000000000099",
    },
    vehicle: {
      make: "Suzuki",
      model: "VXL",
      year: "2025",
      plateNumber: "ASEED3322",
      fuelType: "PETROL",
      mileage: "2436",
      vin: "VIN-TEST-000123456",
      color: "White",
    },
    items: [
      {
        type: "package",
        title: "3D Wheel Alignment",
        code: "PKG-WA",
        description: "Full drain and replacement of alignment settings",
        quantity: 1,
        qtyLabel: "1 Package",
        unitPrice: 250.0,
        discount: 0.0,
        tax: 12.5,
        amount: 262.5,
      },
      {
        type: "service",
        title: "Oil Change Service",
        code: "SRV-OC",
        description: "Engine oil and filter replacement",
        quantity: 1,
        qtyLabel: "1 Service",
        unitPrice: 180.0,
        discount: 10.0,
        tax: 8.5,
        amount: 178.5,
      },
      {
        type: "item",
        title: "Cabin Air Filter",
        code: "ITM-CAF",
        description: "OEM cabin filter replacement",
        quantity: 1,
        qtyLabel: "1 Item",
        unitPrice: 85.0,
        discount: 0.0,
        tax: 4.25,
        amount: 89.25,
      },
    ],
    subtotal: 515.0,
    discount: 10.0,
    netSubtotal: 505.0,
    tax: 25.25,
    total: 530.25,
    paidAmount: 200.0,
    dueAmount: 330.25,
    payments: [
      {
        // details: "Advance payment",
        number: "PAY-1001",
        date: "2026-10-15",
        mode: "Card",
        amount: 200.0,
      },
    ],
    deliveryNotes: [
      {
        vehicleName: "Suzuki VXL 2025",
        vehiclePlateNo: "ASEED3322",
        deliveryNoteNo: "DN-2026-10-5",
        subtotal: 100.0,
        discount: 0.0,
        tax: 15.0,
        total: 115.0,
      },
      {
        vehicleName: "Toyota Corolla 2024",
        vehiclePlateNo: "DXB-4521",
        deliveryNoteNo: "DN-2026-10-8",
        subtotal: 350.0,
        discount: 20.0,
        tax: 16.5,
        total: 346.5,
      },
    ],
    footerText:
      settings.footerText ||
      "Thank you for choosing InoPressa Automotive Services.",
  };

  const viewData: ModernInvoiceViewData = data ?? dummyData;
  const resolvedLogoSrc = logoSrc || viewData.logoUrl || "";

  const docTitle = getDocTitle();

  const bilingual = (
    primary: string,
    secondary?: string,
    inline = false,
  ): React.ReactNode => {
    if (settings.languageDisplay === "primary") return primary;
    if (settings.languageDisplay === "secondary") return secondary || primary;
    if (!secondary) return primary;
    if (inline) return `${primary} / ${secondary}`;
    return (
      <Box
        component="span"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "inherit",
          lineHeight: 1.25,
        }}
      >
        <span>{primary}</span>
        <span
          className="ff-arabic"
          style={{ opacity: 0.85, fontSize: "0.92em", fontWeight: 600 }}
        >
          {secondary}
        </span>
      </Box>
    );
  };

  const fromPair = (
    pair: { primary?: string; secondary?: string },
    inline = false,
  ) => bilingual(pair.primary || "", pair.secondary, inline);

  /** Summary/table labels that respect languageDisplay (unlike getSummaryLabel(..., true)). */
  const summaryLabel = (key: string) => {
    const custom =
      settings.summaryLabels?.[key as keyof typeof settings.summaryLabels];
    const defaults = getSummaryLabel(key) as {
      primary: string;
      secondary: string;
    };
    return fromPair(
      {
        primary:
          custom?.primary && custom.primary.trim() !== ""
            ? custom.primary
            : defaults.primary,
        secondary:
          custom?.secondary && custom.secondary.trim() !== ""
            ? custom.secondary
            : defaults.secondary,
      },
      true,
    );
  };

  const columnLabel = (key: string) => {
    const custom =
      settings.tableColumnLabels?.[
        key as keyof typeof settings.tableColumnLabels
      ];
    const defaults = getTableColumnLabel(key);
    return fromPair(
      {
        primary:
          custom?.primary && custom.primary.trim() !== ""
            ? custom.primary
            : defaults.primary,
        secondary:
          custom?.secondary && custom.secondary.trim() !== ""
            ? custom.secondary
            : defaults.secondary,
      },
      false,
    );
  };

  const consolidatedColumnLabel = (key: string) => {
    const custom =
      settings.consolidatedTableColumnLabels?.[
        key as keyof typeof settings.consolidatedTableColumnLabels
      ];
    const defaults = getConsolidatedTableColumnLabel(key);
    return fromPair(
      {
        primary:
          custom?.primary && custom.primary.trim() !== ""
            ? custom.primary
            : defaults.primary,
        secondary:
          custom?.secondary && custom.secondary.trim() !== ""
            ? custom.secondary
            : defaults.secondary,
      },
      false,
    );
  };

  const isConsolidatedColumnVisible = (key: string) => {
    const cols = settings.consolidatedTableColumns;
    if (!cols) return false;
    if (key === "total") {
      if (cols.total != null) return !!cols.total;
      return !!cols.totalWithTax;
    }
    return !!cols[key as keyof typeof cols];
  };

  const vehicleLabel = (field: string) => {
    const custom =
      settings.vehicleLabels?.[field as keyof typeof settings.vehicleLabels];
    const defaults = getVehicleLabel(field);
    return fromPair(
      {
        primary:
          custom?.primary && custom.primary.trim() !== ""
            ? custom.primary
            : defaults.primary,
        secondary:
          custom?.secondary && custom.secondary.trim() !== ""
            ? custom.secondary
            : defaults.secondary,
      },
      true,
    );
  };

  const renderItemContent = (item: ModernInvoiceViewData["items"][number]) => {
    const opts = settings.itemDisplayOptions || {
      showTitle:
        settings.descriptionDisplay === "title" ||
        settings.descriptionDisplay === "all" ||
        !settings.descriptionDisplay,
      showCode:
        settings.descriptionDisplay === "code" ||
        settings.descriptionDisplay === "all" ||
        !settings.descriptionDisplay,
      showDescription:
        settings.descriptionDisplay === "description" ||
        settings.descriptionDisplay === "all",
    };
    const showTitle = opts.showTitle ?? true;
    const showCode = opts.showCode ?? true;
    const showDescription = opts.showDescription ?? false;
    const parts: string[] = [];
    if (showTitle && showCode) parts.push(`${item.title} - ${item.code}`);
    else if (showTitle) parts.push(item.title);
    else if (showCode) parts.push(item.code);

    return (
      <Box>
        {parts.length ? (
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            {parts.join(" ")}
          </Typography>
        ) : null}
        {showDescription && item.description ? (
          <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.25 }}>
            {item.description}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const formatDate = (date: string) =>
    dayjs(date).format(settings.dateFormat || "DD-MM-YYYY");

  const currencyCode = (currencySymbol || "AED").trim().toUpperCase();

  /** Line-item / table amounts — number only, no currency. */
  const formatAmount = (n: number) =>
    n.toLocaleString("en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  /**
   * Summary / paid / due — currency on the right of the number (e.g. "515.00 AED").
   * Forced LTR so bilingual layout does not reorder the parts.
   */
  const moneyWithCurrency = (n: number) => (
    <Box
      component="span"
      dir="ltr"
      sx={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: "4px",
        unicodeBidi: "isolate",
        whiteSpace: "nowrap",
      }}
    >
      <Box component="span" sx={{ fontWeight: "inherit" }}>
        {formatAmount(n)}
      </Box>
      <Box component="span" sx={{ fontWeight: "inherit" }}>
        {currencyCode}
      </Box>
    </Box>
  );

  const showNumberSetting =
    templateType === 3
      ? settings.showJobcardNumber
      : templateType === 4
        ? settings.showWorkorderNumber
        : templateType === 6
          ? settings.showDeliveryNoteNumber
          : isProforma
            ? settings.showProformaInvoiceNumber
            : settings.showInvoiceNumber;

  const numberPosition =
    (templateType === 3
      ? settings.jobcardNumberPosition
      : templateType === 4
        ? settings.workorderNumberPosition
        : templateType === 6
          ? settings.deliveryNoteNumberPosition
          : isProforma
            ? settings.proformaInvoiceNumberPosition
            : settings.invoiceNumberPosition) ||
    (showNumberSetting !== false ? "left" : "none");

  const numberFontSize =
    (templateType === 3
      ? settings.jobcardNumberFontSize
      : templateType === 4
        ? settings.workorderNumberFontSize
        : templateType === 6
          ? settings.deliveryNoteNumberFontSize
          : isProforma
            ? settings.proformaInvoiceNumberFontSize
            : settings.invoiceNumberFontSize) || 26;

  const numberFontWeight =
    (templateType === 3
      ? settings.jobcardNumberFontWeight
      : templateType === 4
        ? settings.workorderNumberFontWeight
        : templateType === 6
          ? settings.deliveryNoteNumberFontWeight
          : isProforma
            ? settings.proformaInvoiceNumberFontWeight
            : settings.invoiceNumberFontWeight) || 800;

  const showNumber = showNumberSetting !== false && numberPosition !== "none";

  const logoPosition =
    settings.logoPosition || (settings.showLogo !== false ? "right" : "none");
  const qrPosition =
    settings.qrPosition || (settings.showQR ? "right" : "none");
  const showLogo = settings.showLogo !== false && logoPosition !== "none";
  const showQR = !!settings.showQR && qrPosition !== "none";

  const sectionOrder = settings.sectionOrder?.length
    ? settings.sectionOrder
    : getDefaultSectionOrderForTemplateType(templateType);

  const Pill = ({
    children,
    bg = C.badgeBg,
    color = C.badgeText,
    uppercase = true,
  }: {
    children: React.ReactNode;
    bg?: string;
    color?: string;
    uppercase?: boolean;
  }) => (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.35,
        borderRadius: "999px",
        backgroundColor: bg,
        color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: uppercase ? "uppercase" : "none",
        lineHeight: 1.4,
      }}
    >
      {children}
    </Box>
  );

  const renderLogoBrand = () => {
    const alignItems =
      logoPosition === "left"
        ? "flex-start"
        : logoPosition === "center"
          ? "center"
          : "flex-end";
    const textAlign =
      logoPosition === "left"
        ? "left"
        : logoPosition === "center"
          ? "center"
          : "right";
    return (
      <Box
        key="logo-brand"
        sx={{
          flexShrink: 0,
          maxWidth: 280,
          display: "flex",
          flexDirection: "column",
          alignItems,
          gap: 0.75,
          textAlign,
        }}
      >
        {resolvedLogoSrc ? (
          <Box
            component="img"
            src={resolvedLogoSrc}
            alt={viewData.company.name || "logo"}
            sx={{
              maxWidth: settings.logoWidth || 150,
              maxHeight: settings.logoHeight || 80,
              objectFit: "contain",
            }}
          />
        ) : viewData.company.name ? (
          <Typography
            sx={{
              fontSize: 21,
              fontWeight: 800,
              color: brand,
              fontFamily: "Saira, sans-serif",
              lineHeight: 1.1,
            }}
          >
            {viewData.company.name}
          </Typography>
        ) : null}
        {viewData.company.tagline ? (
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 600,
              color: C.muted,
              letterSpacing: "0.08em",
            }}
          >
            {viewData.company.tagline}
          </Typography>
        ) : null}
        {settings.companyFields?.vat !== false && viewData.company.vat ? (
          <Pill bg={C.trnChipBg} color={C.text} uppercase={false}>
            {viewData.company.vat}
          </Pill>
        ) : null}
      </Box>
    );
  };

  const renderQRBox = () => (
    <Box
      key="qr"
      sx={{
        width: 64,
        height: 64,
        border: `1px dashed ${C.border}`,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: C.sectionBg,
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: 9, fontWeight: 700, color: C.muted }}>
        QR
      </Typography>
    </Box>
  );

  /** Document title, invoice number, date, and status. */
  const renderDocMeta = (align: "left" | "center" | "right" = "left") => {
    const alignItems =
      align === "center"
        ? "center"
        : align === "right"
          ? "flex-end"
          : "flex-start";
    return (
      <Box
        key="doc-meta"
        sx={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems,
          textAlign: align,
          gap: 0.75,
        }}
      >
        <Pill>{bilingual(docTitle.primary, docTitle.secondary, true)}</Pill>
        {showNumber ? (
          <Typography
            sx={{
              fontSize: numberFontSize,
              fontWeight: numberFontWeight,
              color: brand,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              fontFamily: "Saira, sans-serif",
            }}
          >
            {viewData.invoiceNo}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            flexWrap: "wrap",
            fontSize: 11,
            color: C.text,
            justifyContent: alignItems,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 0.5,
              color: C.muted,
              fontWeight: 700,
            }}
          >
            {bilingual(t("date"), "التاريخ", true)}
            <span>:</span>
          </Box>
          <Typography
            component="span"
            sx={{ fontSize: 11, fontWeight: 700, color: C.text }}
          >
            {formatDate(viewData.date)}
          </Typography>
        </Box>
        {!isProforma && templateType !== 5 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
              fontSize: 11,
              justifyContent: alignItems,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: C.muted,
                fontWeight: 700,
              }}
            >
              {bilingual(t("status"), "الحالة", true)}
              <span>:</span>
            </Box>
            <Pill bg="#FCE4EC" color="#AD1457">
              {bilingual(viewData.status, viewData.statusAr, true)}
            </Pill>
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderLogoSection = () => {
    if (!showLogo && !showNumber && !showQR) return null;

    const isCombined = settings.showInvoiceDirection === "column";
    const numberAlign: "left" | "center" | "right" =
      numberPosition === "center"
        ? "center"
        : numberPosition === "right"
          ? "right"
          : "left";
    const docMeta = renderDocMeta(numberAlign);

    const column = (
      items: React.ReactNode[],
      align: "flex-start" | "center" | "flex-end",
    ) => (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: align,
          gap: 1,
        }}
      >
        {items}
      </Box>
    );

    // Separate: logo on its own row, then number/meta (+ QR) on the next row
    if (!isCombined) {
      const logoJustify =
        logoPosition === "center"
          ? "center"
          : logoPosition === "right"
            ? "flex-end"
            : "flex-start";
      const numberJustify =
        numberPosition === "center"
          ? "center"
          : numberPosition === "right"
            ? "flex-end"
            : "flex-start";

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mb: 2.5,
            width: "100%",
          }}
        >
          {showLogo ? (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: logoJustify,
                alignItems: "flex-start",
              }}
            >
              {renderLogoBrand()}
            </Box>
          ) : null}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: showQR ? "space-between" : numberJustify,
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: numberJustify,
                flex: showQR ? 1 : undefined,
                minWidth: 0,
              }}
            >
              {docMeta}
            </Box>
            {showQR ? renderQRBox() : null}
          </Box>
        </Box>
      );
    }

    // Combine: shared header row — place logo / number / QR by their positions
    const leftItems: React.ReactNode[] = [];
    const centerItems: React.ReactNode[] = [];
    const rightItems: React.ReactNode[] = [];

    const place = (pos: string | undefined, el: React.ReactNode) => {
      if (!el || !pos || pos === "none") return;
      if (pos === "left") leftItems.push(el);
      else if (pos === "center") centerItems.push(el);
      else if (pos === "right") rightItems.push(el);
    };

    place(logoPosition, showLogo ? renderLogoBrand() : null);
    place(
      numberPosition && numberPosition !== "none" ? numberPosition : "left",
      docMeta,
    );
    place(qrPosition, showQR ? renderQRBox() : null);

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: 2.5,
          width: "100%",
        }}
      >
        {column(leftItems, "flex-start")}
        {column(centerItems, "center")}
        {column(rightItems, "flex-end")}
      </Box>
    );
  };

  const partyBlock = (
    title: string,
    titleAr: string,
    party: {
      name: string;
      phone?: string;
      email?: string;
      address?: string;
      vat?: string;
      tagline?: string;
    },
    fields?: CustomSettings["companyFields"] | CustomSettings["customerFields"],
  ) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: C.muted,
          letterSpacing: "0.06em",
          mb: 0.75,
          textTransform: "uppercase",
          textAlign: "left",
          width: "100%",
        }}
      >
        {bilingual(title, titleAr, true)}
      </Typography>
      {fields?.name !== false ? (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: C.text,
            mb: 0.5,
            textAlign: "left",
            width: "100%",
          }}
        >
          {party.name || ""}
        </Typography>
      ) : null}
      {fields?.email !== false && "email" in party && party.email ? (
        <Typography
          sx={{
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            textAlign: "left",
            width: "100%",
          }}
        >
          {party.email}
        </Typography>
      ) : null}
      {fields?.phone !== false && party.phone ? (
        <Typography
          sx={{
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            textAlign: "left",
            width: "100%",
          }}
        >
          {party.phone}
        </Typography>
      ) : null}
      {fields?.address !== false && party.address ? (
        <Typography
          sx={{
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            textAlign: "left",
            width: "100%",
          }}
        >
          {party.address}
        </Typography>
      ) : null}
      {fields?.vat && "vat" in party && party.vat ? (
        <Typography
          sx={{
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            mt: 0.5,
            textAlign: "left",
            width: "100%",
          }}
        >
          {party.vat}
        </Typography>
      ) : null}
    </Box>
  );

  const renderHeaderSection = () => {
    if (settings.showHeader === false) return null;
    const showCompany = settings.showCompanySection !== false;
    const showCustomer = settings.showCustomerSection !== false;
    if (!showCompany && !showCustomer) return null;
    return (
      <Box
        sx={{
          backgroundColor: C.sectionBg,
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          display: "flex",
          gap: 0,
          flexWrap: "nowrap",
          alignItems: "stretch",
        }}
      >
        {showCompany
          ? partyBlock(
              "SERVICE PROVIDER",
              "مزود الخدمة",
              viewData.company,
              // Modern: TRN stays under logo only — never in company block
              { ...settings.companyFields, vat: false },
            )
          : null}
        {showCompany && showCustomer ? (
          <Box
            sx={{
              width: "1px",
              alignSelf: "stretch",
              backgroundColor: C.border,
              mx: 2.5,
              flexShrink: 0,
            }}
          />
        ) : null}
        {showCustomer
          ? partyBlock(
              "BILL TO",
              "فاتورة إلى",
              viewData.customer,
              settings.customerFields,
            )
          : null}
      </Box>
    );
  };

  const renderVehicleSection = () => {
    if (
      settings.showVehicleInfo === false ||
      isProforma ||
      settings.invoiceType === "consolidated"
    )
      return null;

    const vf = settings.vehicleFields || {};
    const enabled = Object.entries(vf).filter(([, val]) => val);
    if (enabled.length === 0) return null;

    const v = viewData.vehicle;
    const title = [
      vf.make ? v.make : null,
      vf.model ? v.model : null,
      vf.year ? v.year : null,
    ]
      .filter(Boolean)
      .join(" ")
      .toUpperCase();

    const extraFields = enabled.filter(
      ([key]) =>
        key !== "make" &&
        key !== "model" &&
        key !== "year" &&
        key !== "plateNumber" &&
        key !== "vin",
    );

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1.5,
          py: 1.25,
          borderTop: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {title ? (
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                {title}
              </Typography>
            ) : null}
            {vf.plateNumber ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  border: `2px solid ${C.plateBorder}`,
                  borderRadius: "999px",
                  px: 1.5,
                  py: 0.4,
                  backgroundColor: "#fff",
                  margin: "0px 5px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 800,
                    fontSize: 12,
                    color: C.text,
                    letterSpacing: "0.04em",
                  }}
                >
                  {v.plateNumber}
                </Typography>
                <Typography
                  className="ff-arabic"
                  sx={{ fontSize: 10, fontWeight: 700, color: C.muted }}
                >
                  {vehicleLabel("plateNumber")}
                </Typography>
              </Box>
            ) : null}
          </Box>
          {vf.vin && v.vin ? (
            <Typography
              sx={{
                fontSize: 11,
                color: C.muted,
                fontWeight: 600,
                letterSpacing: "0.02em",
              }}
            >
              <Box
                component="span"
                sx={{ color: C.muted, fontWeight: 700, mr: 0.75 }}
              >
                {vehicleLabel("vin")}:
              </Box>
              {v.vin}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {extraFields.map(([key]) => {
            const value = v[key as keyof typeof v];
            if (value == null || value === "") return null;
            return (
              <Box key={key} sx={{ textAlign: "right" }}>
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: C.muted,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {vehicleLabel(key)}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: C.text, fontWeight: 700 }}
                >
                  {key === "mileage"
                    ? `${Number(value).toLocaleString()} Km`
                    : String(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const thSx = {
    backgroundColor: C.tableHead,
    color: C.muted,
    fontSize: 9,
    fontWeight: 700,
    textAlign: "left" as const,
    px: 1,
    py: 1,
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    lineHeight: 1.25,
    verticalAlign: "bottom" as const,
  };

  const tdSx = {
    fontSize: 11,
    color: C.text,
    px: 1,
    py: 1.25,
    borderBottom: `1px solid ${C.border}`,
    verticalAlign: "top" as const,
  };

  const renderTableSection = () => {
    if (settings.showTable === false || templateType === 5) return null;

    if (settings.invoiceType === "consolidated") {
      const moneyCols = new Set([
        "subtotal",
        "discount",
        "discountedSubtotal",
        "tax",
        "total",
      ]);

      const consolidatedTh = (
        key: string,
        align: "left" | "right" = "left",
        width?: string | number,
      ) => (
        <Box
          component="th"
          sx={{
            ...thSx,
            textAlign: align,
            ...(width != null ? { width, minWidth: width } : { minWidth: 72 }),
          }}
        >
          {consolidatedColumnLabel(key)}
        </Box>
      );

      const header = (
        <Box component="tr">
          {isConsolidatedColumnVisible("serialNo")
            ? consolidatedTh("serialNo", "left", 52)
            : null}
          {isConsolidatedColumnVisible("vehicleName")
            ? consolidatedTh("vehicleName", "left", 140)
            : null}
          {isConsolidatedColumnVisible("vehiclePlateNo")
            ? consolidatedTh("vehiclePlateNo", "left", 110)
            : null}
          {isConsolidatedColumnVisible("deliveryNoteNo")
            ? consolidatedTh("deliveryNoteNo", "left", 120)
            : null}
          {isConsolidatedColumnVisible("subtotal")
            ? consolidatedTh("subtotal", "right", 88)
            : null}
          {isConsolidatedColumnVisible("discount")
            ? consolidatedTh("discount", "right", 80)
            : null}
          {isConsolidatedColumnVisible("discountedSubtotal")
            ? consolidatedTh("discountedSubtotal", "right", 100)
            : null}
          {isConsolidatedColumnVisible("tax")
            ? consolidatedTh("tax", "right", 72)
            : null}
          {isConsolidatedColumnVisible("total")
            ? consolidatedTh("total", "right", 88)
            : null}
        </Box>
      );

      const rows = viewData.deliveryNotes.map((note, index) => {
        const cells: { key: string; value: React.ReactNode }[] = [
          { key: "serialNo", value: index + 1 },
          { key: "vehicleName", value: note.vehicleName },
          { key: "vehiclePlateNo", value: note.vehiclePlateNo },
          { key: "deliveryNoteNo", value: note.deliveryNoteNo },
          { key: "subtotal", value: formatAmount(note.subtotal) },
          { key: "discount", value: formatAmount(note.discount) },
          {
            key: "discountedSubtotal",
            value: formatAmount(note.subtotal - note.discount),
          },
          { key: "tax", value: formatAmount(note.tax) },
          { key: "total", value: formatAmount(note.total) },
        ];

        return (
          <Box component="tr" key={`${note.deliveryNoteNo}-${index}`}>
            {cells.map(({ key, value }) => {
              if (!isConsolidatedColumnVisible(key)) return null;
              return (
                <Box
                  component="td"
                  key={key}
                  sx={{
                    ...tdSx,
                    textAlign: moneyCols.has(key) ? "right" : "left",
                    fontWeight: key === "total" ? 700 : undefined,
                  }}
                >
                  {value}
                </Box>
              );
            })}
          </Box>
        );
      });

      return (
        <Box sx={{ mb: 2.5, overflowX: "auto" }}>
          <Box
            sx={{
              border: `1.5px solid ${C.text}`,
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 720,
                borderCollapse: "collapse",
                tableLayout: "auto",
                border: "none",
                "& tbody tr:last-of-type td": {
                  borderBottom: "none",
                },
              }}
            >
              <Box component="thead">{header}</Box>
              <Box component="tbody">{rows}</Box>
            </Box>
          </Box>
        </Box>
      );
    }

    const cols = settings.tableColumns || {};

    const th = (
      key: string,
      align: "left" | "right" | "center" = "left",
      width?: string | number,
    ) => (
      <Box
        component="th"
        sx={{
          ...thSx,
          textAlign: align,
          ...(width != null ? { width, minWidth: width } : { minWidth: 72 }),
        }}
      >
        {columnLabel(key)}
      </Box>
    );

    const renderHeader = () => (
      <Box component="tr">
        {cols.serialNo ? th("serialNo", "left", 52) : null}
        {cols.item ? th("item", "left", 160) : null}
        {cols.quantity ? th("quantity", "left", 80) : null}
        {cols.unitPrice ? th("unitPrice", "right", 88) : null}
        {cols.amount ? th("amount", "right", 88) : null}
        {cols.discount ? th("discount", "right", 80) : null}
        {cols.discountedSubtotal
          ? th("discountedSubtotal", "right", 100)
          : null}
        {cols.tax ? th("tax", "right", 72) : null}
        {cols.totalWithTax ? th("totalWithTax", "right", 88) : null}
      </Box>
    );

    const renderRow = (
      item: ModernInvoiceViewData["items"][number],
      idx: number,
    ) => {
      const baseAmount = item.unitPrice * item.quantity;
      const discountedSubtotal = baseAmount - item.discount;
      const totalWithTax = discountedSubtotal + item.tax;
      const typeMark =
        item.type === "item" ? "Ⓘ" : item.type === "service" ? "Ⓢ" : "Ⓟ";
      return (
        <Box component="tr" key={`${item.code}-${idx}`}>
          {cols.serialNo ? (
            <Box component="td" sx={tdSx}>
              {typeMark} {idx + 1}
            </Box>
          ) : null}
          {cols.item ? (
            <Box component="td" sx={tdSx}>
              {renderItemContent(item)}
            </Box>
          ) : null}
          {cols.quantity ? (
            <Box component="td" sx={tdSx}>
              {item.qtyLabel}
            </Box>
          ) : null}
          {cols.unitPrice ? (
            <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
              {formatAmount(item.unitPrice)}
            </Box>
          ) : null}
          {cols.amount ? (
            <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
              {formatAmount(baseAmount)}
            </Box>
          ) : null}
          {cols.discount ? (
            <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
              {formatAmount(item.discount)}
            </Box>
          ) : null}
          {cols.discountedSubtotal ? (
            <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
              {formatAmount(discountedSubtotal)}
            </Box>
          ) : null}
          {cols.tax ? (
            <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
              {formatAmount(item.tax)}
            </Box>
          ) : null}
          {cols.totalWithTax ? (
            <Box
              component="td"
              sx={{ ...tdSx, textAlign: "right", fontWeight: 700 }}
            >
              {formatAmount(totalWithTax)}
            </Box>
          ) : null}
        </Box>
      );
    };

    const renderTable = (
      rows: ModernInvoiceViewData["items"],
      section?: "packages" | "services" | "items",
    ) => {
      if (!rows.length) return null;
      const sectionLbl = section ? getTableSectionLabel(section) : null;
      return (
        <Box sx={{ mb: section ? 2 : 0 }}>
          {sectionLbl ? (
            <Typography
              sx={{ fontSize: 12, fontWeight: 800, color: brand, mb: 1 }}
            >
              {fromPair(sectionLbl, true)}
            </Typography>
          ) : null}
          <Box
            sx={{
              border: `1.5px solid ${C.text}`,
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 720,
                borderCollapse: "collapse",
                tableLayout: "auto",
                border: "none",
                "& tbody tr:last-of-type td": {
                  borderBottom: "none",
                },
              }}
            >
              <Box component="thead">{renderHeader()}</Box>
              <Box component="tbody">
                {rows.map((item, idx) => renderRow(item, idx))}
              </Box>
            </Box>
          </Box>
        </Box>
      );
    };

    if (settings.tableLayout === "separate") {
      return (
        <Box sx={{ mb: 2.5, overflowX: "auto" }}>
          {renderTable(
            viewData.items.filter(
              (i) => i.type === "package" && settings.showPackages,
            ),
            "packages",
          )}
          {renderTable(
            viewData.items.filter(
              (i) => i.type === "service" && settings.showServices,
            ),
            "services",
          )}
          {renderTable(
            viewData.items.filter(
              (i) => i.type === "item" && settings.showItems,
            ),
            "items",
          )}
        </Box>
      );
    }

    const combined = viewData.items.filter((i) => {
      if (i.type === "package") return settings.showPackages !== false;
      if (i.type === "service") return settings.showServices !== false;
      if (i.type === "item") return settings.showItems !== false;
      return true;
    });

    return (
      <Box sx={{ mb: 2.5, overflowX: "auto" }}>{renderTable(combined)}</Box>
    );
  };

  const summaryRow = (
    label: React.ReactNode,
    value: React.ReactNode,
    opts?: {
      total?: boolean;
      split?: boolean;
      paid?: React.ReactNode;
      due?: React.ReactNode;
    },
  ) => {
    if (opts?.split) {
      return (
        <Box sx={{ display: "flex", gap: 0.75, mt: 0.75 }}>
          <Box
            sx={{
              flex: 1,
              backgroundColor: C.paidBg,
              borderRadius: "8px",
              px: 1.5,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: C.text }}>
              {summaryLabel("paidAmount")}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: C.paidText,
                direction: "ltr",
                unicodeBidi: "isolate",
              }}
            >
              {opts.paid}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              backgroundColor: C.dueBg,
              borderRadius: "8px",
              px: 1.5,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: C.text }}>
              {summaryLabel("dueAmount")}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: C.dueText,
                direction: "ltr",
                unicodeBidi: "isolate",
              }}
            >
              {opts.due}
            </Typography>
          </Box>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1.5,
          py: opts?.total ? 1.25 : 0.9,
          backgroundColor: opts?.total ? C.totalBg : C.summaryRowBg,
          borderRadius: opts?.total ? "8px" : "4px",
          color: opts?.total ? C.totalText : C.text,
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: opts?.total ? 13 : 12,
            fontWeight: opts?.total ? 800 : 600,
            color: opts?.total ? C.totalText : C.text,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: opts?.total ? 15 : 12,
            fontWeight: 800,
            color: opts?.total ? C.totalText : C.text,
            direction: "ltr",
            unicodeBidi: "isolate",
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const renderSummarySection = () => {
    if (templateType === 5) return null;
    const s = settings.summaryItems || {};
    const showPayments =
      settings.showPaymentInfo !== false && !isProforma && templateType !== 5;

    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          mb: 2.5,
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            flex: "1 1 240px",
            minWidth: 200,
            border: `1px dashed ${C.border}`,
            borderRadius: "12px",
            p: 2,
            backgroundColor: "#FAFBFD",
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: C.muted,
              letterSpacing: "0.05em",
              mb: 1.5,
            }}
          >
            {bilingual(
              "CUSTOMER ACCEPTANCE & SIGNATURE",
              "قبول وتوقيع العميل",
              true,
            )}
          </Typography>
          <Box
            sx={{
              height: 72,
              borderBottom: `1px solid ${C.border}`,
              mb: 1,
              position: "relative",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 200 60"
              sx={{ width: 140, height: 48, opacity: 0.55 }}
            >
              <path
                d="M10 40 C40 10, 70 50, 100 25 S160 10, 190 35"
                fill="none"
                stroke={brand}
                strokeWidth="2"
              />
            </Box>
          </Box>
          <Typography sx={{ fontSize: 9, color: C.muted, fontWeight: 600 }}>
            {bilingual("VERIFIED ON RECORD", "تم التحقق في السجل", true)}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: "1 1 320px",
            minWidth: 260,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Box>
            {s.subtotal !== false
              ? summaryRow(
                  summaryLabel("subtotal"),
                  moneyWithCurrency(viewData.subtotal),
                )
              : null}
            {s.discount !== false
              ? summaryRow(
                  summaryLabel("discount"),
                  moneyWithCurrency(viewData.discount),
                )
              : null}
            {s.discountedSubtotal !== false
              ? summaryRow(
                  summaryLabel("discountedSubtotal"),
                  moneyWithCurrency(viewData.netSubtotal),
                )
              : null}
            {s.tax !== false
              ? summaryRow(summaryLabel("tax"), moneyWithCurrency(viewData.tax))
              : null}
            {s.total !== false
              ? summaryRow(
                  summaryLabel("total"),
                  moneyWithCurrency(viewData.total),
                  {
                    total: true,
                  },
                )
              : null}
            {showPayments
              ? summaryRow(null, "", {
                  split: true,
                  paid: moneyWithCurrency(viewData.paidAmount),
                  due: moneyWithCurrency(viewData.dueAmount),
                })
              : null}
          </Box>
          {showPayments ? renderPayments() : null}
        </Box>
      </Box>
    );
  };

  const renderPayments = () => {
    if (settings.showPaymentInfo === false || isProforma || templateType === 5)
      return null;
    if (data && !viewData.payments.length) return null;
    return (
      <Box sx={{ width: "100%" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            border: `1px solid ${C.border}`,
            tableLayout: "fixed",
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {[
                // fromPair(getPaymentLabel()),
                bilingual("Payment Number", "رقم الدفع"),
                bilingual("Payment Date", "تاريخ الدفع"),
                bilingual("Payment Mode", "طريقة الدفع"),
                bilingual("Payment Amount", "مبلغ الدفع"),
              ].map((h, i) => (
                <Box
                  component="th"
                  key={i}
                  sx={{
                    backgroundColor: C.paymentHead,
                    color: C.paymentHeadText,
                    fontSize: 9,
                    fontWeight: 700,
                    textAlign: i === 4 ? "right" : "left",
                    px: 1,
                    py: 0.9,
                    verticalAlign: "bottom",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.25,
                  }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {viewData.payments.map((p) => (
              <Box component="tr" key={p.number}>
                {/* <Box component="td" sx={{ ...tdSx, fontSize: 10 }}>
                  {p.details}
                </Box> */}
                <Box component="td" sx={{ ...tdSx, fontSize: 10 }}>
                  {p.number}
                </Box>
                <Box component="td" sx={{ ...tdSx, fontSize: 10 }}>
                  {formatDate(p.date)}
                </Box>
                <Box component="td" sx={{ ...tdSx, fontSize: 10 }}>
                  {p.mode}
                </Box>
                <Box
                  component="td"
                  sx={{
                    ...tdSx,
                    fontSize: 10,
                    textAlign: "right",
                    fontWeight: 700,
                  }}
                >
                  {formatAmount(p.amount)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderTermsSection = () => {
    if (settings.showTerms === false) return null;
    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          pt: 2,
          borderTop: `1px solid ${C.border}`,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              backgroundColor: C.sectionBg,
              color: C.muted,
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            i
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: 10,
              color: C.muted,
              lineHeight: 1.55,
              whiteSpace: "pre-line",
              textAlign: "left",
            }}
          >
            {viewData.termsText || fromPair(getTermsLabel(), true)}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "1px",
            backgroundColor: C.border,
            alignSelf: "stretch",
          }}
        />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 1,
            direction: "rtl",
            alignItems: "flex-start",
            justifyContent: "flex-start",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              backgroundColor: C.sectionBg,
              color: C.muted,
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            i
          </Box>
          <Typography
            className="ff-arabic"
            sx={{
              flex: 1,
              fontSize: 10,
              color: C.muted,
              lineHeight: 1.55,
              whiteSpace: "pre-line",
              textAlign: "right",
            }}
          >
            {viewData.termsTextSecondary ||
              (!data
                ? "جميع الخدمات وقطع الغيار تخضع لشروط وأحكام الورشة. تطبق الضمان وفق سياسة الشركة المصنعة."
                : "")}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderNotesSection = () => {
    if (settings.showNotes === false) return null;
    if (data && !viewData.notesText) return null;
    return (
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: C.sectionBg,
          borderRadius: "8px",
        }}
      >
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, color: C.muted, mb: 0.5 }}
        >
          {bilingual("Notes", "ملاحظات", true)}
        </Typography>
        <Typography sx={{ fontSize: 11, color: C.text }}>
          {viewData.notesText || "Next service recommended at 5,000 Km."}
        </Typography>
      </Box>
    );
  };

  const renderFooterSection = () => {
    if (settings.showFooter === false) return null;
    const footerItems = settings.footerItems || {};
    const activeItems: string[] = [];
    if (footerItems.address) activeItems.push(viewData.company.address);
    if (footerItems.email) activeItems.push(viewData.company.email);
    if (footerItems.phone) activeItems.push(viewData.company.phone);
    if (footerItems.socialLinks)
      activeItems.push("Facebook | Twitter | Instagram");

    return (
      <Box sx={{ pt: 1.5, borderTop: `1px solid ${C.border}` }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Typography sx={{ fontSize: 11, color: C.muted, flex: 1 }}>
            {viewData.footerText}
          </Typography>
          <Typography
            className="ff-arabic"
            sx={{ fontSize: 11, color: C.muted, flex: 1, textAlign: "right" }}
          >
            شكرًا لاختياركم خدمات إينوبريسا للسيارات.
          </Typography>
        </Box>
        {activeItems.length || footerItems.pageNumber ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
                flex: 1,
              }}
            >
              {activeItems.map((item, index) => (
                <React.Fragment key={`${item}-${index}`}>
                  <Typography sx={{ fontSize: 10, color: C.muted }}>
                    {item}
                  </Typography>
                  {index < activeItems.length - 1 ? (
                    <Typography sx={{ fontSize: 10, color: "#999" }}>
                      |
                    </Typography>
                  ) : null}
                </React.Fragment>
              ))}
            </Box>
            {footerItems.pageNumber ? (
              <Typography sx={{ fontSize: 10, color: C.muted }}>
                {fromPair(getFooterLabel("pageNumber"), true)} 1
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderInspection = () => {
    if (templateType !== 5 || settings.showInspectionItems === false)
      return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1, color: brand }}>
          {bilingual("Inspection Items", "بنود الفحص", true)}
        </Typography>
        <Box
          component="table"
          sx={{ width: "100%", borderCollapse: "collapse" }}
        >
          <Box component="thead">
            <Box component="tr">
              {["Part", "Status", "Notes"].map((h) => (
                <Box component="th" key={h} sx={thSx}>
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {[
              { part: "Part 1", status: "Need Repair" },
              { part: "Part 2", status: "Broken / Missing" },
              { part: "Part 3", status: "Working" },
            ].map((r) => (
              <Box component="tr" key={r.part}>
                <Box component="td" sx={tdSx}>
                  {r.part}
                </Box>
                <Box component="td" sx={tdSx}>
                  {r.status}
                </Box>
                <Box component="td" sx={tdSx}>
                  —
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSection = (sectionId: string) => {
    switch (sectionId) {
      case "logo":
        return (
          <React.Fragment key="logo">{renderLogoSection()}</React.Fragment>
        );
      case "header":
        return (
          <React.Fragment key="header">{renderHeaderSection()}</React.Fragment>
        );
      case "vehicleInfo":
        return (
          <React.Fragment key="vehicleInfo">
            {renderVehicleSection()}
          </React.Fragment>
        );
      case "table":
        return (
          <React.Fragment key="table">{renderTableSection()}</React.Fragment>
        );
      case "summary":
        return (
          <React.Fragment key="summary">
            {renderSummarySection()}
          </React.Fragment>
        );
      case "terms":
        return (
          <React.Fragment key="terms">{renderTermsSection()}</React.Fragment>
        );
      case "notes":
        return (
          <React.Fragment key="notes">{renderNotesSection()}</React.Fragment>
        );
      case "footer":
        return (
          <React.Fragment key="footer">{renderFooterSection()}</React.Fragment>
        );
      case "inspectionItems":
        return (
          <React.Fragment key="inspectionItems">
            {renderInspection()}
          </React.Fragment>
        );
      case "vehicleImage":
      case "customerSignature":
      case "inspectionRemarks":
        return null;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "center",
        backgroundColor: C.pageBg,
      }}
      data-design-variant="modern"
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 980,
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(16, 24, 40, 0.06)",
          border: `1px solid ${C.border}`,
          p: { xs: 2, sm: 3 },
          fontFamily: "Saira, Inter, sans-serif",
        }}
      >
        {sectionOrder.map((id) => renderSection(id))}
      </Box>
    </Box>
  );
};

export default ModernInvoicePreview;
